import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ShieldAlert, Plus, Loader2, Trash2, Pencil, Sparkles, CheckCircle, X,
} from 'lucide-react';
import { aiActApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const CATEGORIES = [
  { value: 'technical', label: 'Técnico' },
  { value: 'ethical', label: 'Ético' },
  { value: 'legal', label: 'Legal' },
  { value: 'operational', label: 'Operacional' },
  { value: 'bias', label: 'Viés (Bias)' },
  { value: 'drift', label: 'Drift do modelo' },
  { value: 'adversarial', label: 'Adversarial' },
  { value: 'privacy', label: 'Privacidade' },
];

const STATUSES = [
  { value: 'open', label: 'Aberto' },
  { value: 'mitigated', label: 'Mitigado' },
  { value: 'accepted', label: 'Aceite' },
  { value: 'closed', label: 'Fechado' },
];

const init = {
  system_id: '', risk_category: 'technical', risk_description: '',
  likelihood: 3, impact: 3, mitigation_plan: '', residual_likelihood: '', residual_impact: '',
  owner_id: '', review_date: '',
};

export default function AiRisks() {
  const [params] = useSearchParams();
  const initialSystemId = params.get('system_id') || '';
  const autoOpenSuggest = params.get('ai') === '1';
  const [risks, setRisks] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...init, system_id: initialSystemId });
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [systemFilter, setSystemFilter] = useState(initialSystemId);
  const [statusFilter, setStatusFilter] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestSystemId, setSuggestSystemId] = useState(initialSystemId);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [dismissed, setDismissed] = useState({});
  const [acceptingIdx, setAcceptingIdx] = useState(null);

  useEffect(() => { setSuggestSystemId(systemFilter); }, [systemFilter]);
  useEffect(() => { if (autoOpenSuggest && !loading && systems.length) setShowSuggest(true); /* eslint-disable-next-line */ }, [autoOpenSuggest, loading, systems.length]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [systemFilter, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const p = {};
      if (systemFilter) p.system_id = systemFilter;
      if (statusFilter) p.status = statusFilter;
      const [rs, sy] = await Promise.all([
        aiActApi.listRisks(p),
        aiActApi.listSystems({}),
      ]);
      setRisks(rs.data);
      setSystems(sy.data);
    } catch { toast.error('Erro ao carregar riscos IA'); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.system_id) { toast.error('Selecione um sistema IA'); return; }
    if (!form.risk_description.trim()) { toast.error('Descrição obrigatória'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        likelihood: Number(form.likelihood),
        impact: Number(form.impact),
        residual_likelihood: form.residual_likelihood ? Number(form.residual_likelihood) : null,
        residual_impact: form.residual_impact ? Number(form.residual_impact) : null,
        review_date: form.review_date || null,
      };
      if (editing) {
        await aiActApi.updateRisk(editing.ai_risk_id, payload);
        toast.success('Risco atualizado');
      } else {
        await aiActApi.createRisk(payload);
        toast.success('Risco registado');
      }
      setShowForm(false); setEditing(null); setForm({ ...init, system_id: initialSystemId }); load();
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const handleEdit = (r) => {
    setEditing(r);
    setForm({
      system_id: r.system_id, risk_category: r.risk_category, risk_description: r.risk_description,
      likelihood: r.likelihood, impact: r.impact, mitigation_plan: r.mitigation_plan || '',
      residual_likelihood: r.residual_likelihood ?? '', residual_impact: r.residual_impact ?? '',
      owner_id: r.owner_id || '', review_date: r.review_date || '',
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!delTarget) return;
    try {
      await aiActApi.deleteRisk(delTarget.ai_risk_id);
      toast.success('Risco eliminado');
      setDelTarget(null); load();
    } catch { toast.error('Erro ao eliminar'); }
  };

  const getSysName = (sid) => systems.find(s => s.system_id === sid)?.name || sid;

  const runSuggest = async () => {
    if (!suggestSystemId) { toast.error('Selecione um sistema IA'); return; }
    setSuggesting(true);
    setSuggestions([]);
    setDismissed({});
    try {
      const res = await aiActApi.suggestRisks(suggestSystemId, { language: 'pt-PT' });
      setSuggestions(res.data.suggestions || []);
      if (res.data.suggestions?.length === 0) toast.info('O modelo não devolveu sugestões.');
      else toast.success(`${res.data.suggestions.length} sugestões geradas por ${res.data.model}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar sugestões IA');
    } finally { setSuggesting(false); }
  };

  const acceptSuggestion = async (idx) => {
    const s = suggestions[idx];
    if (!s) return;
    setAcceptingIdx(idx);
    try {
      await aiActApi.createRisk({
        system_id: suggestSystemId,
        risk_category: s.risk_category,
        risk_description: s.risk_description,
        likelihood: s.likelihood,
        impact: s.impact,
        mitigation_plan: s.mitigation_plan,
      });
      toast.success('Risco aceite e registado');
      setDismissed({ ...dismissed, [idx]: 'accepted' });
      load();
    } catch { toast.error('Erro ao aceitar sugestão'); }
    finally { setAcceptingIdx(null); }
  };

  const editAndAccept = (idx) => {
    const s = suggestions[idx];
    if (!s) return;
    setEditing(null);
    setForm({
      system_id: suggestSystemId,
      risk_category: s.risk_category,
      risk_description: s.risk_description,
      likelihood: s.likelihood,
      impact: s.impact,
      mitigation_plan: s.mitigation_plan,
      residual_likelihood: '', residual_impact: '', owner_id: '', review_date: '',
    });
    setShowSuggest(false);
    setShowForm(true);
  };

  if (loading) return <Layout title="EU AI Act — Riscos"><div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin" /></div></Layout>;

  return (
    <Layout title="EU AI Act — Registo de Riscos (Art. 9)" headerContent={
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setShowSuggest(true)} data-testid="ai-suggest-btn">
          <Sparkles className="h-4 w-4 mr-2 text-indigo-600" />Sugestões IA
        </Button>
        <Button onClick={() => { setEditing(null); setForm({ ...init, system_id: systemFilter }); setShowForm(true); }} data-testid="new-risk-btn">
          <Plus className="h-4 w-4 mr-2" />Novo Risco
        </Button>
      </div>
    }>
      <div className="space-y-6" data-testid="ai-risks-container">

        <Card className="card-flat">
          <CardContent className="p-4 flex gap-3 flex-wrap">
            <Select value={systemFilter || 'all'} onValueChange={v => setSystemFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[250px]" data-testid="filter-risk-system"><SelectValue placeholder="Todos os sistemas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os sistemas</SelectItem>
                {systems.map(s => <SelectItem key={s.system_id} value={s.system_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[160px]" data-testid="filter-risk-status"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {risks.length === 0 ? (
          <Card className="card-flat"><CardContent className="p-8 text-center">
            <ShieldAlert className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum risco registado</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="risks-list">
            {risks.map(r => (
              <Card key={r.ai_risk_id} className="card-flat hover:shadow-sm transition-shadow" data-testid={`airisk-${r.ai_risk_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      'h-12 w-12 rounded-lg flex items-center justify-center font-bold',
                      r.risk_score >= 20 ? 'bg-red-100 text-red-700' :
                      r.risk_score >= 10 ? 'bg-orange-100 text-orange-700' :
                      'bg-green-100 text-green-700'
                    )}>{r.risk_score}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono text-xs text-slate-400">{r.ai_risk_id}</span>
                        <Badge variant="outline" className="text-xs">{CATEGORIES.find(c => c.value === r.risk_category)?.label || r.risk_category}</Badge>
                        <Badge variant="outline" className="text-xs">{STATUSES.find(s => s.value === r.status)?.label || r.status}</Badge>
                        <span className="text-xs text-slate-500">L:{r.likelihood} × I:{r.impact}</span>
                        {r.residual_score != null && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Residual: {r.residual_score}</Badge>
                        )}
                      </div>
                      <p className="font-medium text-slate-900">{r.risk_description}</p>
                      <p className="text-xs text-slate-500 mt-1">Sistema: {getSysName(r.system_id)}</p>
                      {r.mitigation_plan && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{r.mitigation_plan}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(r)} data-testid={`edit-risk-${r.ai_risk_id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setDelTarget(r)} data-testid={`delete-risk-${r.ai_risk_id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Risco IA' : 'Novo Risco IA'}</DialogTitle>
            <DialogDescription>Registo de risco no âmbito do Art. 9 — sistema de gestão de risco</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Sistema IA *</Label>
              <Select value={form.system_id} onValueChange={v => setForm({ ...form, system_id: v })}>
                <SelectTrigger data-testid="risk-form-system"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{systems.map(s => <SelectItem key={s.system_id} value={s.system_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={form.risk_category} onValueChange={v => setForm({ ...form, risk_category: v })}>
                <SelectTrigger data-testid="risk-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrição do risco *</Label>
              <Textarea rows={3} value={form.risk_description} onChange={e => setForm({ ...form, risk_description: e.target.value })} required data-testid="risk-form-desc" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Probabilidade (1-5)</Label>
                <Input type="number" min="1" max="5" value={form.likelihood} onChange={e => setForm({ ...form, likelihood: e.target.value })} data-testid="risk-form-likelihood" />
              </div>
              <div className="space-y-2">
                <Label>Impacto (1-5)</Label>
                <Input type="number" min="1" max="5" value={form.impact} onChange={e => setForm({ ...form, impact: e.target.value })} data-testid="risk-form-impact" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Plano de mitigação</Label>
              <Textarea rows={3} value={form.mitigation_plan} onChange={e => setForm({ ...form, mitigation_plan: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Probabilidade Residual</Label>
                <Input type="number" min="1" max="5" value={form.residual_likelihood} onChange={e => setForm({ ...form, residual_likelihood: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Impacto Residual</Label>
                <Input type="number" min="1" max="5" value={form.residual_impact} onChange={e => setForm({ ...form, residual_impact: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Data de revisão</Label>
              <Input type="date" value={form.review_date} onChange={e => setForm({ ...form, review_date: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-risk-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editing ? 'Guardar' : 'Registar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showSuggest} onOpenChange={(v) => { setShowSuggest(v); if (!v) { setSuggestions([]); setDismissed({}); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              Sugestões IA de Riscos
            </DialogTitle>
            <DialogDescription>
              O Motor de IA analisa a documentação técnica, governança de dados e classificação do sistema
              IA para propor 3-5 riscos relevantes (Art. 9). Aceite, edite ou descarte cada sugestão.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Sistema IA a analisar</Label>
              <Select value={suggestSystemId} onValueChange={setSuggestSystemId} disabled={suggesting}>
                <SelectTrigger data-testid="suggest-system-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{systems.map(s => <SelectItem key={s.system_id} value={s.system_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <Button onClick={runSuggest} disabled={suggesting || !suggestSystemId} className="w-full" data-testid="run-suggest-btn">
              {suggesting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analisando documentação (~15s)...</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" />{suggestions.length > 0 ? 'Re-gerar Sugestões' : 'Gerar Sugestões'}</>
              )}
            </Button>

            {suggesting && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-indigo-700">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
                Modelo IA a analisar o sistema. Pode demorar até 30 segundos.
              </div>
            )}

            {suggestions.length > 0 && (
              <div className="space-y-3" data-testid="suggestions-list">
                {suggestions.map((s, idx) => {
                  const status = dismissed[idx];
                  if (status === 'discarded') return null;
                  const accepted = status === 'accepted';
                  return (
                    <div
                      key={idx}
                      className={cn(
                        'border rounded-lg p-4',
                        accepted ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'
                      )}
                      data-testid={`suggestion-${idx}`}
                    >
                      <div className="flex items-start gap-3 mb-2 flex-wrap">
                        <div className={cn(
                          'h-10 w-10 rounded-lg flex items-center justify-center font-bold text-sm shrink-0',
                          s.likelihood * s.impact >= 20 ? 'bg-red-100 text-red-700' :
                          s.likelihood * s.impact >= 10 ? 'bg-orange-100 text-orange-700' :
                          'bg-green-100 text-green-700'
                        )}>
                          {s.likelihood * s.impact}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge variant="outline" className="text-xs">{CATEGORIES.find(c => c.value === s.risk_category)?.label || s.risk_category}</Badge>
                            <span className="text-xs text-slate-500">L:{s.likelihood} × I:{s.impact}</span>
                            {accepted && (
                              <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                                <CheckCircle className="h-3 w-3 mr-1" />Aceite
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium text-slate-900">{s.risk_description}</p>
                          {s.rationale && (
                            <p className="text-xs text-slate-500 italic mt-2">
                              <Sparkles className="h-3 w-3 inline mr-1 text-indigo-600" />
                              {s.rationale}
                            </p>
                          )}
                          <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-700">
                            <strong>Mitigação:</strong> {s.mitigation_plan}
                          </div>
                        </div>
                      </div>
                      {!accepted && (
                        <div className="flex items-center gap-2 justify-end mt-3 pt-3 border-t border-slate-100">
                          <Button variant="ghost" size="sm" onClick={() => setDismissed({ ...dismissed, [idx]: 'discarded' })} data-testid={`discard-${idx}`}>
                            <X className="h-4 w-4 mr-1" />Descartar
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => editAndAccept(idx)} data-testid={`edit-accept-${idx}`}>
                            <Pencil className="h-4 w-4 mr-1" />Editar e aceitar
                          </Button>
                          <Button size="sm" onClick={() => acceptSuggestion(idx)} disabled={acceptingIdx === idx} data-testid={`accept-${idx}`}>
                            {acceptingIdx === idx ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                            Aceitar
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
              <strong>Aviso:</strong> As sugestões são geradas por IA e devem ser sempre revistas por um especialista humano (Compliance Officer / DPO).
              Não constituem aconselhamento legal.
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={v => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Risco IA</AlertDialogTitle>
            <AlertDialogDescription>Confirma a eliminação deste risco?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="confirm-delete-risk">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
